import { useState } from 'react'
import { useCreateNoticia, useUpdateNoticia } from '@/hooks/useNoticias'
import { X, Loader2, Save, Image as ImageIcon, Link as LinkIcon } from 'lucide-react'
import type { NoticiaPortal, NoticiaTipo, UUID } from '@/types/database'

interface NoticiaFormProps {
    noticia: NoticiaPortal | null
    onClose: () => void
}

export function NoticiaForm({ noticia, onClose }: NoticiaFormProps) {
    const createMut = useCreateNoticia()
    const updateMut = useUpdateNoticia()
    const isPending = createMut.isPending || updateMut.isPending

    const [titulo, setTitulo] = useState(noticia?.titulo || '')
    const [conteudo, setConteudo] = useState(noticia?.conteudo || '')
    const [tipo, setTipo] = useState<NoticiaTipo>(noticia?.tipo || 'noticia')
    const [imagemUrl, setImagemUrl] = useState(noticia?.imagem_url || '')
    const [ordem, setOrdem] = useState(String(noticia?.ordem || '1'))
    const [ativo, setAtivo] = useState(noticia?.ativo ?? true)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const payload = {
            titulo,
            conteudo,
            tipo,
            imagem_url: imagemUrl || null,
            ordem: parseInt(ordem),
            ativo
        }

        try {
            if (noticia) {
                await updateMut.mutateAsync({ id: noticia.id as UUID, ...payload })
            } else {
                await createMut.mutateAsync(payload)
            }
            onClose()
        } catch (err) {
            console.error(err)
            alert('Erro ao salvar notícia')
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
            <div className="bg-white w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden rounded-3xl shadow-elevated">
                {/* Header */}
                <div className="p-4 border-b border-surface-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-surface-900">
                        {noticia ? `Editar Notícia` : 'Nova Notícia / Aviso'}
                    </h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Form */}
                <form id="noticia-form" onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="label">Título da Notícia</label>
                            <input
                                type="text" required value={titulo} onChange={e => setTitulo(e.target.value)}
                                className="input font-semibold" placeholder="Ex: Nova parceria com oficinas parceiras"
                            />
                        </div>

                        <div>
                            <label className="label">Tipo de Conteúdo</label>
                            <select
                                value={tipo} onChange={e => setTipo(e.target.value as any)}
                                className="input"
                            >
                                <option value="noticia">📰 Notícia</option>
                                <option value="aviso">⚠️ Aviso / Alerta</option>
                                <option value="informacao">ℹ️ Informação Útil</option>
                            </select>
                        </div>

                        <div>
                            <label className="label">Ordem de Exibição</label>
                            <input
                                type="number" required value={ordem} onChange={e => setOrdem(e.target.value)}
                                className="input"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="label">Conteúdo (Texto)</label>
                            <textarea
                                required value={conteudo} onChange={e => setConteudo(e.target.value)}
                                className="input min-h-[150px] leading-relaxed"
                                placeholder="Descreva os detalhes da notícia..."
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="label">URL da Imagem</label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <LinkIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                                    <input
                                        type="url" value={imagemUrl} onChange={e => setImagemUrl(e.target.value)}
                                        className="input pl-9" placeholder="https://exemplo.com/imagem.jpg"
                                    />
                                </div>
                                <div className="w-12 h-[42px] rounded-lg border border-surface-200 bg-white flex items-center justify-center shrink-0 overflow-hidden text-surface-400">
                                    {imagemUrl ? <img src={imagemUrl} className="w-full h-full object-cover" /> : <ImageIcon size={20} />}
                                </div>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <label className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 bg-white cursor-pointer">
                                <input
                                    type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)}
                                    className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
                                />
                                <div>
                                    <span className="font-semibold text-surface-900">Conteúdo Visível</span>
                                    <p className="text-[10px] text-surface-500 font-medium">Marcando como inativo, ele sairá do portal do associado imediatamente.</p>
                                </div>
                            </label>
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="p-4 border-t border-surface-100 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="btn border border-surface-200 bg-white text-surface-700">
                        Cancelar
                    </button>
                    <button type="submit" form="noticia-form" disabled={isPending}
                        className="btn btn-primary gap-2 shadow-lg shadow-primary-500/20">
                        {isPending ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                        Publicar Conteúdo
                    </button>
                </div>
            </div>
        </div>
    )
}
